/**
 * Create — pre-lobby settings.
 *
 * Admin chooses difficulty (easy/medium/hard) and turn timer (off/60s/120s)
 * via chalk-pencil radio stamps, then taps START GAME which mints a fresh
 * session locally (until Stream A's session-create endpoint lands) and
 * routes to the lobby with `?role=admin`.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { surfaces, fontStacks } from '@qd/design-tokens';
import type { DifficultyKey } from '@qd/protocol';
import { SlateBackground } from '../components/base/SlateBackground';
import { ChalkButton } from '../components/base/ChalkButton';
import { ChalkText } from '../components/base/ChalkText';
import { apiFetch, prewarmApi } from '../net/apiFetch';
import { useIsMobile } from '../hooks/useIsMobile';

type Difficulty = DifficultyKey;
type TurnTimer = 'off' | '60s' | '120s';

/**
 * API base URL.
 *
 * Resolution order (first match wins):
 *   1. VITE_API_BASE_URL (explicit override; used in production)
 *   2. VITE_WS_BASE_URL with `wss?://` rewritten to `https?://` (single
 *      source of truth — prevents the drift that happens when only one
 *      of the two env vars gets configured for a deploy target)
 *   3. http://localhost:8787 (local Wrangler default)
 */
function getApiBaseUrl(): string {
  const env = (import.meta as unknown as {
    env?: { VITE_API_BASE_URL?: string; VITE_WS_BASE_URL?: string };
  }).env;
  if (env?.VITE_API_BASE_URL) return env.VITE_API_BASE_URL;
  if (env?.VITE_WS_BASE_URL) return env.VITE_WS_BASE_URL.replace(/^ws/, 'http');
  return 'http://localhost:8787';
}

export function Create() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [difficulty, setDifficulty] = useState<Difficulty>('medium');
  const [timer, setTimer] = useState<TurnTimer>('60s');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-warm the deployed Worker so the actual click pays no cold-start
  // latency. Fires once on mount; no-op if the user navigates away
  // before the response.
  useEffect(() => {
    prewarmApi(getApiBaseUrl());
  }, []);

  async function handleStart() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      // Mint a session on the server. The returned `sessionId` (which IS
      // the human-readable word-code, e.g. "CHALK-PENCIL-23") becomes the
      // GameRoom DO room name when we open the WebSocket inside <Lobby/>.
      // apiFetch retries on transient failures (cold start, TLS hiccup,
      // network blip) before surfacing an error to the user.
      const res = await apiFetch(`${getApiBaseUrl()}/api/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Settings are applied AFTER the WS handshake via UpdateSettingsMsg
        // — the server doesn't accept them on the create endpoint yet.
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error(`Server responded ${res.status}`);
      const body = (await res.json()) as { sessionId: string; sessionCode: string };
      navigate(`/game/${body.sessionId}/lobby?role=admin`, {
        state: { settings: { difficulty, turnTimer: timer } },
      });
    } catch (e) {
      setError(
        e instanceof Error && e.name === 'TimeoutError'
          ? 'The server took too long to respond. Try again in a moment.'
          : 'Couldn’t reach the server. Check your connection and try again.',
      );
      setSubmitting(false);
    }
  }

  return (
    <SlateBackground>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '100dvh',
          padding: isMobile ? '32px 16px' : '64px 24px',
          gap: isMobile ? 32 : 48,
        }}
      >
        <ChalkText size="heading" underline>
          Pre-Match Setup
        </ChalkText>

        <section
          aria-label="Difficulty"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 20,
          }}
        >
          <SectionLabel>Difficulty</SectionLabel>
          <StampGroup
            options={[
              { value: 'easy', label: 'Easy' },
              { value: 'medium', label: 'Medium' },
              { value: 'hard', label: 'Hard' },
            ]}
            value={difficulty}
            onChange={(v) => setDifficulty(v as Difficulty)}
            ariaLabel="Difficulty"
            compact={isMobile}
          />
        </section>

        <section
          aria-label="Turn timer"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 20,
          }}
        >
          <SectionLabel>Turn Timer</SectionLabel>
          <StampGroup
            options={[
              { value: 'off', label: 'Off' },
              { value: '60s', label: '60s' },
              { value: '120s', label: '120s' },
            ]}
            value={timer}
            onChange={(v) => setTimer(v as TurnTimer)}
            ariaLabel="Turn timer"
            compact={isMobile}
          />
        </section>

        <ChalkButton
          size="lg"
          variant="primary"
          onClick={handleStart}
          disabled={submitting}
        >
          {submitting ? 'Minting…' : 'Start Game'}
        </ChalkButton>
        {error && (
          <span
            role="alert"
            style={{
              fontFamily: fontStacks.mono,
              fontSize: 12,
              letterSpacing: '0.18em',
              color: '#C44826',
              opacity: 0.85,
            }}
          >
            {error}
          </span>
        )}

        <ChalkButton
          size="md"
          variant="secondary"
          onClick={() => navigate('/')}
          style={{ marginTop: 24, fontSize: 16 }}
        >
          ← Back to Main Menu
        </ChalkButton>
      </div>
    </SlateBackground>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        fontFamily: fontStacks.mono,
        fontSize: 12,
        letterSpacing: '0.32em',
        color: surfaces.chalk,
        opacity: 0.7,
      }}
    >
      {children}
    </span>
  );
}

interface StampOption {
  value: string;
  label: string;
}
interface StampGroupProps {
  options: StampOption[];
  value: string;
  onChange: (v: string) => void;
  ariaLabel: string;
  compact?: boolean;
}

/** A row of three chalk-pencil radio stamps. The selected one fills with
 *  vermillion paper; unselected ones are graphite outlines. Click toggles
 *  selection, matching native radio semantics. */
function StampGroup({ options, value, onChange, ariaLabel, compact }: StampGroupProps) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      style={{
        display: 'flex',
        gap: compact ? 12 : 16,
        flexWrap: 'wrap',
        justifyContent: 'center',
      }}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <ChalkButton
            key={opt.value}
            variant={selected ? 'primary' : 'secondary'}
            size="md"
            ariaLabel={`${ariaLabel}: ${opt.label}`}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </ChalkButton>
        );
      })}
    </div>
  );
}
